import React from "react";
import { Link } from "react-router-dom";

import { TextField, SelectField } from "react-admin";

import { Card, CardContent, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { isAuthorized } from "../../../utils/appUtils";
import { AclType, InvoiceStatuses } from "../../../utils/constants";
import { getFormTab } from "../../../utils/utils";
import { hasExternalId } from "../../../utils/zapierUtils";

import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";

import { InvoiceStatusIcon } from "./InvoiceStatusIcon";
import { CopyToClipboardButton } from "../../../components/common/CopyToClipboardButton";

const useAsideStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      width: "18em",
      minWidth: "18em",
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0.5),
      overflow: "initial",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  status: {
    marginTop: theme.spacing(0.3),
  },
  tableIcon: {
    marginTop: theme.spacing(0.7),
  },
}));

export const InvoiceEditAside = ({ backPath, record, ...props }) => {
  const classes = useAsideStyles();
  var tab = getFormTab(props);
  const linkBackPath = backPath
    ? [...backPath, props.location.pathname]
    : [props.location.pathname];

  if (!record || tab === "1" || tab === "charges") return null;

  const inboundShipmentEntries = record.inboundShipmentId
    ? [
        {
          id: record.inboundShipmentId,
          poNumber: record.inboundShipment?.poNumber || record.poNumber || "N/A",
        },
      ]
    : (record.lineItems?.items || [])
        .filter((item) => item.inboundShipmentId)
        .map((item) => ({
          id: item.inboundShipmentId,
          poNumber: item.inboundShipment?.poNumber || "N/A",
        }));
  const outboundShipmentEntries = record.outboundShipmentId
    ? [
        {
          id: record.outboundShipmentId,
          poNumber:
            record.outboundShipment?.poNumber || record.poNumber || "N/A",
        },
      ]
    : (record.lineItems?.items || [])
        .filter((item) => item.outboundShipmentId)
        .map((item) => ({
          id: item.outboundShipmentId,
          poNumber: item.outboundShipment?.poNumber || "N/A",
        }));
  const inboundShipments = [
    ...new Map(inboundShipmentEntries.map((item) => [item.id, item])).values(),
  ];
  const outboundShipments = [
    ...new Map(outboundShipmentEntries.map((item) => [item.id, item])).values(),
  ];

  return (
    <>
      <Card className={classes.root}>
        <CardContent>
          <Grid container direction="column" spacing={1} fullWidth>
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Status
              </Typography>
              <Grid container direction="row">
                <Grid item xs={2}>
                  <InvoiceStatusIcon record={record} />
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <SelectField
                    source="status"
                    choices={InvoiceStatuses}
                    record={record}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Account
              </Typography>
              <br />
              {isAuthorized(AclType.ACCOUNTS) ? (
                <Link
                  to={{
                    pathname: `/accounts/${record.accountId}`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {accountNameRenderer(record.account)}
                  </Typography>
                </Link>
              ) : (
                <Link
                  to={{
                    pathname: `/accounts/${record.accountId}/show`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {record.account.businessName}
                  </Typography>
                </Link>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                PO Number
              </Typography>
              <br />
              <TextField source="poNumber" record={record} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Invoice Number
              </Typography>
              <br />
              <TextField source="invoiceNumber" record={record} />
              <CopyToClipboardButton text={record.invoiceNumber} />
            </Grid>
            {hasExternalId(record) && (
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  External ID
                </Typography>
                <br />
                <TextField source="externalId" record={record} />
              </Grid>
            )}
            {(inboundShipments?.length > 0 ||
              outboundShipments?.length > 0 ||
              record.inboundShipmentId ||
              record.outboundShipmentId) && (
              <>
                {/* Inbound Shipments */}
                {inboundShipments.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="overline" color="textSecondary">
                      Inbound Shipments:
                    </Typography>
                    <br />
                    {inboundShipments.map((inbound) => (
                      <Link
                        key={inbound.id}
                        to={{
                          pathname: `/inboundShipments/${inbound.id}`,
                          state: { backPath: linkBackPath },
                        }}
                      >
                        <Typography variant="body2" color="primary">
                          PO no. {inbound.poNumber}
                        </Typography>
                      </Link>
                    ))}
                  </Grid>
                )}

                {/* Outbound Shipments */}
                {outboundShipments.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="overline" color="textSecondary">
                      Outbound Shipments:
                    </Typography>
                    <br />
                    {outboundShipments.map((outbound) => (
                      <Link
                        key={outbound.id}
                        to={{
                          pathname: `/outboundShipments/${outbound.id}`,
                          state: { backPath: linkBackPath },
                        }}
                      >
                        <Typography variant="body2" color="primary">
                          PO no. {outbound.poNumber}
                        </Typography>
                      </Link>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};
